import { useRef, useState } from "react";
import { bringIntoView } from "../../ui/scroll";
import type { Menu } from "../../api/types";
import { t } from "../../i18n";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { categoryColor } from "../sale/menu-view";
import { CategoryDialog } from "./CategoryDialog";
import { alphabeticalOrder, hasOwnOrder, moveCategory, moveProduct, ownOrder, productsOf } from "./menu-edit";
import { ProductEditor } from "./ProductEditor";
import { ProductRows } from "./ProductRows";
import type { MenuEditor } from "./useMenuEditor";
import { useQuickPrices } from "./useQuickPrices";

interface Props {
  editor: MenuEditor;
  focus: { productId: string; allergens: boolean } | null;
}

/**
 * Producten, as in the prototype: categories on the left (colour, number of products, ↑ ↓ ✎ and +),
 * the products of the chosen category or the search results in the middle, the editor on the right.
 *
 * The products of a category stand in the order the shop gives them (Furkan, 03/10/2026): a row is held
 * and slid to its place, or the category is put back in the alphabet. The till, the Tafo app and every
 * list follow that order at once.
 */
export function ProductsTab({ editor, focus }: Props) {
  const { menu, soldOut, toast } = editor;
  const text = t.menuAdmin.order;
  const prices = useQuickPrices(editor);
  const focused = focus ? menu.products[focus.productId] : undefined;
  const [categoryId, setCategoryId] = useState(() => focused?.categoryId ?? menu.categories[0]?.id ?? "");
  const [selected, setSelected] = useState<string | null>(focused?.id ?? null);
  const editorBox = useRef<HTMLDivElement>(null);
  /** On a phone the settings stand under the long list of products: a chosen product brings them in sight. */
  const showEditor = () => {
    if (typeof window.matchMedia !== "function" || !window.matchMedia("(max-width: 700px)").matches) return;
    requestAnimationFrame(() => {
      if (editorBox.current) bringIntoView(editorBox.current, { block: "start", smooth: true });
    });
  };
  const [query, setQuery] = useState("");
  const [categoryDialog, setCategoryDialog] = useState<{ id: string | null } | null>(null);
  const [toAlphabet, setToAlphabet] = useState(false);
  // switching to the shop's own order is asked too, as the alphabet is (Furkan, 18/10/2026)
  const [toOwn, setToOwn] = useState(false);
  /** The order a row of a category was just let go in: it stands at once, while the server stores it. */
  const [letGo, setLetGo] = useState<{ menu: Menu; categoryId: string; ids: string[] } | null>(null);

  const category = menu.categories.find((candidate) => candidate.id === categoryId) ?? menu.categories[0];
  const search = query.trim().toLowerCase();
  const listed = search
    ? Object.values(menu.products)
        .filter((product) => product.categoryId !== null)
        .filter(
          (product) =>
            product.name.toLowerCase().includes(search) || (product.code ?? "").toLowerCase().includes(search),
        )
    : category
      ? productsOf(menu, category.id)
      : [];
  const products =
    !search && letGo?.menu === menu && letGo.categoryId === category?.id
      ? letGo.ids.flatMap((id) => (Object.hasOwn(menu.products, id) ? [menu.products[id]!] : []))
      : listed;
  const count = (id: string) => Object.values(menu.products).filter((product) => product.categoryId === id).length;
  const current = selected === null || selected === "new" ? null : (menu.products[selected] ?? null);

  // the rows of one category can be put in another order; search results are of several categories
  const sortable = !search && category !== undefined;
  const own = category !== undefined && hasOwnOrder(menu, category.id);
  /** A row was let go at another place: from now on the category stands in the shop's own order. */
  const moveRow = (id: string, to: number) => {
    const product = menu.products[id];
    const next = moveProduct(menu, id, to);
    if (!category || !product || next === menu) return;
    const ids = productsOf(next, category.id).map((item) => item.id);
    const place = ids.indexOf(id) + 1;
    setLetGo({ menu, categoryId: category.id, ids });
    void editor.commit(next, text.logMoved(category.name, product.name, place)).then((saved) => {
      if (saved) toast(text.moved(product.name, place, ids.length));
      else setLetGo(null);
    });
  };

  /** A phone: the moved category stays in sight in the row that slides sideways. */
  const followChosen = () => {
    requestAnimationFrame(() => {
      const chosen = document.querySelector<HTMLElement>(".mcatlist .mcatrow.on");
      if (chosen) bringIntoView(chosen, { inline: "nearest", smooth: true });
    });
  };

  return (
    <div className="m3">
      <div className="mcol mcats">
        <div className="mcolh">
          <b>{t.menuAdmin.categories}</b>
          <button
            type="button"
            className="btn small"
            title={t.menuAdmin.newCategory}
            aria-label={t.menuAdmin.newCategory}
            onClick={() => {
              setCategoryDialog({ id: null });
            }}
          >
            +
          </button>
        </div>
        {/* a phone: one row that slides sideways (Furkan, 18/10/2026: the wrapped chips looked untidy) */}
        <div className="mcatlist">
          {menu.categories.map((item, index) => {
            const on = item.id === category?.id && !search;
            return (
              <div key={item.id} className={`mcatrow${on ? " on" : ""}`}>
                <button
                  type="button"
                  className="mcatsel"
                  aria-pressed={on}
                  onClick={(event) => {
                    setCategoryId(item.id);
                    setQuery("");
                    // a phone: the chosen chip grows (← → ✎) and slides fully into sight
                    const row = event.currentTarget.parentElement;
                    requestAnimationFrame(() => {
                      if (row) bringIntoView(row, { inline: "nearest", smooth: true });
                    });
                    if (current && current.categoryId !== item.id) setSelected(null);
                  }}
                >
                  <span className="cdot" style={{ background: categoryColor(item, index) }} />
                  <span className="mcn">{item.name}</span>
                  <span className="mcc">{count(item.id)}</span>
                </button>
                {on && (
                  <span className="mcact">
                    <button
                      type="button"
                      className="btn small"
                      disabled={index === 0 || editor.busy}
                      aria-label={t.menuAdmin.up}
                      onClick={() =>
                        void editor
                          .commit(moveCategory(menu, item.id, -1), `Categorie ${item.name} omhoog`)
                          .then(followChosen)
                      }
                    >
                      <span className="dir-v">↑</span>
                      <span className="dir-h" aria-hidden="true">
                        ←
                      </span>
                    </button>
                    <button
                      type="button"
                      className="btn small"
                      disabled={index === menu.categories.length - 1 || editor.busy}
                      aria-label={t.menuAdmin.down}
                      onClick={() =>
                        void editor
                          .commit(moveCategory(menu, item.id, 1), `Categorie ${item.name} omlaag`)
                          .then(followChosen)
                      }
                    >
                      <span className="dir-v">↓</span>
                      <span className="dir-h" aria-hidden="true">
                        →
                      </span>
                    </button>
                    <button
                      type="button"
                      className="btn small"
                      aria-label={t.menuAdmin.editCategory}
                      onClick={() => {
                        setCategoryDialog({ id: item.id });
                      }}
                    >
                      ✎
                    </button>
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mcol mplist">
        <div className="mcolh">
          <b>{search ? t.menuAdmin.results(products.length) : (category?.name ?? "")}</b>
          {!search && category && (
            <span className="note">
              {t.menuAdmin.vatLine(
                category.vat?.takeaway ?? 6,
                category.vat?.onSite ?? 12,
                category.webSplitBySize === true,
                category.tillSplitBySize === true,
              )}
            </span>
          )}
        </div>
        <input
          placeholder={t.menuAdmin.search}
          aria-label={t.menuAdmin.search}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        {sortable && (
          <div className="morder">
            <div className="seg" role="group" aria-label={text.label}>
              <button
                type="button"
                aria-pressed={!own}
                disabled={editor.busy}
                onClick={() => {
                  if (own) setToAlphabet(true);
                }}
              >
                {text.alphabetical}
              </button>
              <button
                type="button"
                aria-pressed={own}
                disabled={editor.busy}
                onClick={() => {
                  if (!own) setToOwn(true);
                }}
              >
                {text.own}
              </button>
            </div>
            <span className="note">{text.hint}</span>
          </div>
        )}
        <ProductRows
          menu={menu}
          products={products}
          sortable={sortable}
          busy={editor.busy}
          selected={selected}
          soldOut={soldOut}
          prices={prices}
          onOpen={(product) => {
            setSelected(product.id);
            if (product.categoryId) setCategoryId(product.categoryId);
            showEditor();
          }}
          onMove={moveRow}
        />
        {!search && category && (
          <button
            type="button"
            className="btn"
            onClick={() => {
              setSelected("new");
              showEditor();
            }}
          >
            {t.menuAdmin.addProduct(category.name)}
          </button>
        )}
      </div>

      <div className="mcol medit" ref={editorBox}>
        {selected === null || !category ? (
          <div className="mempty">
            <b>{t.menuAdmin.emptyTitle}</b>
            <span className="note">{t.menuAdmin.emptyText}</span>
          </div>
        ) : (
          <ProductEditor
            key={`${selected}:${String(menu.products[selected] !== undefined)}`}
            editor={editor}
            product={current}
            categoryId={current?.categoryId ?? category.id}
            showAllergens={focus?.allergens === true && focus.productId === selected}
            onSaved={(id, toCategory) => {
              setSelected(id);
              setCategoryId(toCategory);
            }}
            onClose={() => {
              setSelected(null);
            }}
          />
        )}
      </div>

      {toAlphabet && category && (
        <ConfirmDialog
          message={text.confirmAlphabetical(category.name)}
          confirm={text.toAlphabetical}
          tone="primary"
          onConfirm={() => {
            void editor
              .commit(alphabeticalOrder(menu, category.id), text.logAlphabetical(category.name))
              .then((saved) => {
                if (saved) toast(text.nowAlphabetical(category.name));
              });
          }}
          onClose={() => {
            setToAlphabet(false);
          }}
        />
      )}
      {toOwn && category && (
        <ConfirmDialog
          message={text.confirmOwn(category.name)}
          confirm={text.toOwn}
          tone="primary"
          onConfirm={() => {
            void editor.commit(ownOrder(menu, category.id), text.logOwn(category.name)).then((saved) => {
              if (saved) toast(text.nowOwn(category.name));
            });
          }}
          onClose={() => {
            setToOwn(false);
          }}
        />
      )}
      {categoryDialog && (
        <CategoryDialog
          editor={editor}
          category={menu.categories.find((item) => item.id === categoryDialog.id) ?? null}
          onSaved={(id) => {
            setCategoryId(id);
          }}
          onClose={() => {
            setCategoryDialog(null);
          }}
        />
      )}
    </div>
  );
}
